import { useEffect, useState } from "react";
import axios from "axios";
import { Train, RefreshCw, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 60_000;

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).slice(11, 16);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function fmtRelative(secondsAgo) {
  if (secondsAgo < 5) return "just now";
  if (secondsAgo < 60) return `${Math.floor(secondsAgo)}s ago`;
  return `${Math.floor(secondsAgo / 60)}m ago`;
}

/**
 * Live Amtrak arrivals widget. Renders only if the depot has an amtrak_station_code.
 * Auto-refreshes every 60s with a visible "live" indicator so users can see it's updating.
 */
export function AmtrakArrivals({ depotId, stationCode }) {
  const [state, setState] = useState({
    loading: true,
    data: null,
    error: null,
    lastUpdated: null,
    refreshing: false,
  });
  const [tick, setTick] = useState(0); // re-render every 5s so "N s ago" ticks

  useEffect(() => {
    if (!stationCode) return;
    let cancelled = false;

    const load = async () => {
      if (!cancelled) setState((s) => ({ ...s, refreshing: true }));
      try {
        const r = await axios.get(`${API}/amtrak/next/${depotId}`);
        if (!cancelled)
          setState({
            loading: false,
            data: r.data,
            error: null,
            lastUpdated: Date.now(),
            refreshing: false,
          });
      } catch (e) {
        if (!cancelled)
          setState((s) => ({
            ...s,
            loading: false,
            error: "unreachable",
            refreshing: false,
          }));
      }
    };

    load();
    const refreshId = setInterval(load, REFRESH_MS);
    const tickId = setInterval(() => setTick((t) => t + 1), 5000);

    return () => {
      cancelled = true;
      clearInterval(refreshId);
      clearInterval(tickId);
    };
  }, [depotId, stationCode]);

  if (!stationCode) return null;

  const arrivals = state.data?.arrivals || [];
  const secondsAgo =
    state.lastUpdated ? (Date.now() - state.lastUpdated) / 1000 : null;

  return (
    <Card className="bg-[#111] border-white/5" data-testid="amtrak-arrivals-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-white flex items-center gap-2">
            <Train className="w-4 h-4 text-emerald-400" />
            Next Amtrak Trains
          </h4>
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2" aria-hidden="true">
              {state.refreshing && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
              )}
              <span
                className={`relative inline-flex rounded-full h-2 w-2 ${
                  state.error ? "bg-red-500" : "bg-emerald-500"
                }`}
              />
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-400/70 font-mono">
              {stationCode}
            </span>
          </div>
        </div>

        {state.loading && arrivals.length === 0 ? (
          <p className="text-sm text-white/70 py-2">Loading live arrivals…</p>
        ) : state.error || state.data?.error ? (
          <p className="text-sm text-white/70 py-2">
            Live data unavailable — retrying every 60s.
          </p>
        ) : arrivals.length === 0 ? (
          <div className="py-2">
            <p className="text-sm text-white/85">
              No trains currently en route to this station.
            </p>
            <p className="text-xs text-white/70 mt-2 leading-relaxed">
              Amtrak&apos;s live tracker only shows trains once they&apos;ve
              started their run. Tomorrow&apos;s scheduled service will appear
              here as soon as it does.
            </p>
            <a
              href={`https://www.amtrak.com/stations/${stationCode.toLowerCase()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300"
              data-testid="amtrak-schedule-link"
            >
              See full schedule at amtrak.com
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        ) : (
          <div className="space-y-2">
            {arrivals.slice(0, 5).map((a, i) => (
              <div
                key={`${a.train_num}-${i}`}
                className="flex items-baseline justify-between gap-3 border-b border-white/5 last:border-0 pb-2 last:pb-0"
                data-testid={`amtrak-row-${i}`}
              >
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    <span className="font-mono text-emerald-400 mr-2">
                      #{a.train_num}
                    </span>
                    {a.route || "Amtrak"}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5">{a.status}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-sm text-white/90">
                    {fmtTime(a.estimated || a.scheduled)}
                  </p>
                  {a.estimated &&
                    a.scheduled &&
                    a.estimated !== a.scheduled && (
                      <p className="text-[10px] text-white/40 line-through font-mono">
                        {fmtTime(a.scheduled)}
                      </p>
                    )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div
          className="flex items-center justify-between mt-3 pt-2 border-t border-white/5"
          data-testid="amtrak-live-footer"
        >
          <span className="text-xs text-white/60 font-mono tracking-wider">
            amtraker.com
          </span>
          <span
            className="text-xs text-white/70 font-mono tracking-wider inline-flex items-center gap-1"
            /* tick forces re-render so the countdown ticks up */
            data-tick={tick}
          >
            <RefreshCw
              className={`w-3 h-3 ${state.refreshing ? "animate-spin" : ""}`}
            />
            {secondsAgo != null
              ? `Live · updated ${fmtRelative(secondsAgo)}`
              : "Live · refreshes every 60s"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
